import React, { useEffect, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Papa from "papaparse";

import { Card, Paper, Box, Button, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import "./FileUploader.css";
import { useDispatch, useSelector } from "react-redux";
import {
	setCSVBatchData,
	setSelectedItems,
} from "../../redux/slices/contentSlice/contentForm/contentForm";
import useHandleFilePath from "../../hooks/useHandleFilePath";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ViewCSVData from "./ViewCSVData";
import OneDrivePicker from "./OneDrivePicker2";
import onedriveContent from "../../redux/slices/contentSlice/onedriveContent/onedriveContent";
import { urlToFile } from "../../helper/onedrive/urlToFile";
import VideoThumbnail from "./VideoThumbnail";
import { validateCSVHeaders } from "../../helper/csvHeaders";
import { toast } from "react-toastify";

const ItemType = "EPISODE";

function Episode({ episode, index, moveEpisode }) {
	const ref = React.useRef(null);

	const [, drop] = useDrop({
		accept: ItemType,
		hover(item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			if (dragIndex === hoverIndex) {
				return;
			}
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			const hoverMiddleY =
				(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			const clientOffset = monitor.getClientOffset();
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			moveEpisode(dragIndex, hoverIndex);
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: ItemType,
		item: { index },
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	drag(drop(ref));

	return (
		<li
			ref={ref}
			style={{
				opacity: isDragging ? 0.5 : 1,
				display: "flex",
				alignItems: "center",
			}}
			className="episode-item"
		>
			<DragIndicatorIcon />
			{episode.name}
		</li>
	);
}

function EpisodesUploader() {
	const dispatch = useDispatch();
	const contentForm = useSelector((state) => state.contentForm);
	const csvBatchData = useSelector((state) => state.contentForm.csvBatchData);
	console.log(csvBatchData, "csvBatchData");
	const oneDriveFiles = useSelector((state) => state.oneDriveContent.files);
	const sharepoint = useSelector((state) => state.oneDriveContent.sharepoint);
	const access_token = useSelector(
		(state) => state.oneDriveContent.access_token
	);
	const [files, setFiles] = useState([]);
	const [episodes, setEpisodes] = useState([]);
	const [thumbnails, setThumbnails] = useState([]);
	const [metadata, setMetadata] = useState(null);
	const [mappings, setMappings] = useState({});
	const { handleFilePath } = useHandleFilePath();
	const handleFileChange = (event) => {
		const selectedFiles = Array.from(event.target.files);
		setFiles(selectedFiles);
		categorizeFiles(selectedFiles, "local");
	};

	console.log(thumbnails, "thumbnails");
	console.log(metadata, "metadata");

	const fetchPreviewUrl = async (url, token) => {
		try {
			const response = await fetch(url, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			const data = await response.json();
			return data;
		} catch (error) {
			console.error("Error while fetching preview", error);
		}
	};

	const categorizeFiles = async (files, type) => {
		let episodeFiles;
		let thumbnailFiles;
		let metadataFile;

		if (type === "onedrive") {
			const episodeFilesDetails = files.filter((file) =>
				file.file.mimeType.startsWith("video/")
			);
			const episodeFilePromises = episodeFilesDetails.map(
				async (episode) => {
					const thumbnails = await fetchPreviewUrl(
						`${sharepoint}/drives/${episode.parentReference.driveId}/items/${episode.id}/thumbnails`,
						access_token
					);
					const getPosterUrl = thumbnails.value[0].medium.url;
					return {
						name: episode.name,
						downloadUrl: episode["@content.downloadUrl"],
						getPosterUrl,
					};
				}
			);
			episodeFiles = await Promise.all(episodeFilePromises);

			const thumbnailFilesDetails = files.filter((file) =>
				file.file.mimeType.startsWith("image/")
			);
			const thumbnailPromises = thumbnailFilesDetails.map(
				async (thumbnail) => {
					const file = await urlToFile(
						thumbnail["@content.downloadUrl"],
						thumbnail.name,
						thumbnail.file.mimeType
					);
					return file;
				}
			);
			thumbnailFiles = await Promise.all(thumbnailPromises);

			const metadataFileDetails = files.find((file) =>
				file.file.mimeType.endsWith(".ms-excel")
			);
			metadataFile = await urlToFile(
				metadataFileDetails["@content.downloadUrl"],
				metadataFileDetails.name,
				metadataFileDetails.file.mimeType
			);
		} else {
			episodeFiles = files.filter((file) =>
				file.type.startsWith("video/")
			);
			thumbnailFiles = files.filter((file) =>
				file.type.startsWith("image/")
			);
			metadataFile = files.find((file) => file.name.endsWith(".csv"));
		}

		setEpisodes(episodeFiles);
		setThumbnails(thumbnailFiles);
		setMetadata(metadataFile);
		handleCSVData(metadataFile);
	};

	const handleCSVData = (metadataFile) => {
		Papa.parse(metadataFile, {
			complete: (result) => {
				const csvValidatedData = validateCSVHeaders(
					result.data,
					contentForm?.contentType
				);
				if (!csvValidatedData?.isValid) {
					csvValidatedData?.errors?.forEach((error) => {
						toast.warning(error);
					});
				}
				console.log(result.data, "result.data");
				dispatch(setCSVBatchData(csvValidatedData?.mappedHeaders));
			},
			header: true,
			dynamicTyping: true,
			skipEmptyLines: true,
		});
	};

	const handleMappingChange = (episodeName, thumbnailIndex) => {
		const newMappings = { ...mappings };
		newMappings[episodeName] = thumbnails[thumbnailIndex];
		setMappings(newMappings);
	};

	console.log(mappings, "mappings");
	const moveEpisode = (dragIndex, hoverIndex) => {
		const newEpisodes = [...episodes];
		const [movedEpisode] = newEpisodes.splice(dragIndex, 1);
		newEpisodes.splice(hoverIndex, 0, movedEpisode);
		setEpisodes(newEpisodes);
	};

	const handleFilePaths = (files) => {
		const episodesFilePaths = files.map((file, index) => {
			if (index < episodes.length) {
				const title = csvBatchData?.title?.[index]?.value;

				const { fileType, filePath } = handleFilePath(file, title);
				return filePath;
			} else {
				return;
			}
		});
		return episodesFilePaths;
	};

	const handleSave = () => {
		if (!csvBatchData) {
			return;
		}

		let typeOfFile = "";
		if (oneDriveFiles.length > 0) {
			typeOfFile = "onedrive";
		} else {
			typeOfFile = "local";
		}

		const episodesFilePaths = handleFilePaths(episodes);
		const thumbnailsFilePaths = handleFilePaths(thumbnails);
		episodesFilePaths.forEach((filePath, index) => {
			dispatch(
				setSelectedItems({
					filePath: filePath,
					masterFile:
						episodes[index]?.downloadUrl ||
						URL.createObjectURL(episodes[index]),
					posterFilePath: thumbnailsFilePaths[index],
					posterFile: URL.createObjectURL(thumbnails[index]),
					typeOfFile,
				})
			);
		});
	};

	useEffect(() => {
		if (oneDriveFiles.length > 0) {
			categorizeFiles(oneDriveFiles, "onedrive");
		}
	}, [oneDriveFiles]);

	console.log(episodes, "episodes");
	return (
		<DndProvider backend={HTML5Backend}>
			<Paper sx={{ margin: "10px 0" }}>
				<div className="EpisodesUploader">
					<Box display={"flex"} gap={4} alignItems={"center"}>
						<h1>Upload TV Series Season</h1>
						<Button
							component="label"
							role={undefined}
							variant="contained"
							tabIndex={-1}
							sx={{
								height: "50px",
								width: "auto",
							}}
							startIcon={<UploadFileIcon />}
						>
							Batch Upload
							<input
								hidden
								type="file"
								onChange={handleFileChange}
								multiple
							/>
						</Button>
						<OneDrivePicker handleDisableButton={() => false} />
					</Box>
					{/* <input type="file" multiple onChange={handleFileChange} /> */}

					{metadata && (
						<>
							<div className="file-list">
								<h2>Episodes</h2>
								<p>
									List of episodes in order. You can change
									the order of the episodes
								</p>

								<ul>
									{episodes.map((episode, index) => (
										<Episode
											key={episode.name}
											index={index}
											episode={episode}
											moveEpisode={moveEpisode}
										/>
									))}
								</ul>
								<h2>Thumbnails</h2>
								<ul>
									{thumbnails.map((file) => (
										<li key={file.name}>{file.name}</li>
									))}
								</ul>
								<h2>Metadata</h2>
								<Box display={"flex"} alignItems={"center"}>
									{metadata && <p>{metadata.name}</p>}
									<ViewCSVData csvData={csvBatchData} />
								</Box>
							</div>
							<div className="mapping-section">
								<h2>
									List Of Episodes Along With Their Thumbnails
								</h2>
								{episodes.map((episode) => (
									<div key={episode.name} className="mapping">
										<Typography
											component={"span"}
											width={"200px"}
										>
											{episode.name}
										</Typography>
										<select
											onChange={(e) =>
												handleMappingChange(
													episode.name,
													e.target.value
												)
											}
										>
											<option value="">
												Select Thumbnail
											</option>
											{thumbnails.map(
												(thumbnail, index) => (
													<option
														key={thumbnail.name}
														value={index}
													>
														{thumbnail.name}
													</option>
												)
											)}
										</select>
										{mappings[episode.name] ? (
											<img
												src={URL.createObjectURL(
													mappings[episode.name]
												)}
												alt={`Thumbnail for ${episode.name}`}
												width="100"
											/>
										) : episode?.getPosterUrl ? (
											<img
												src={episode.getPosterUrl}
												alt={`Thumbnail for ${episode.name}`}
												width="100"
											/>
										) : (
											<VideoThumbnail file={episode} />
										)}
									</div>
								))}
							</div>
							<Box
								display={"flex"}
								justifyContent={"space-between"}
							>
								<Button
									onClick={() => {
										dispatch(setSelectedItems([]));
										setEpisodes([]);
										setThumbnails([]);
										setFiles([]);
										setMetadata(null);
									}}
									variant="outlined"
								>
									Clear
								</Button>
								<Button
									onClick={handleSave}
									variant="contained"
								>
									{" "}
									Save Changes
								</Button>
							</Box>
						</>
					)}
				</div>
			</Paper>
		</DndProvider>
	);
}

export default EpisodesUploader;
